import { SITE_URL } from "@/lib/routes";

export function personSchema(p) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${SITE_URL}/#person`,
    name: p.name,
    givenName: "Magnus",
    familyName: "Løv Schmidt",
    jobTitle: p.da?.job_title || "SEO Specialist",
    email: `mailto:${p.email}`,
    url: SITE_URL,
    image: p.avatar,
    sameAs: p.same_as || [],
    address: {
      "@type": "PostalAddress",
      streetAddress: p.address?.street,
      postalCode: p.address?.postal_code,
      addressLocality: p.address?.locality,
      addressRegion: p.address?.region,
      addressCountry: p.address?.country,
    },
    worksFor: { "@id": `${SITE_URL}/#business` },
    knowsAbout: [
      "SEO", "Teknisk SEO", "On-page SEO", "Linkbuilding",
      "Ecommerce SEO", "International SEO", "Lokal SEO", "SEO strategi",
    ],
  };
}

export function localBusinessSchema(p, lang = "da") {
  const s = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#business`,
    name: `${p.name} — SEO Specialist`,
    image: p.avatar,
    url: SITE_URL,
    email: `mailto:${p.email}`,
    priceRange: "$$",
    legalName: p.legal_name,
    taxID: p.cvr,
    vatID: p.cvr ? `DK${p.cvr}` : undefined,
    foundingDate: p.founded,
    founder: { "@id": `${SITE_URL}/#person` },
    areaServed: ["Danmark", "Odense", "Fyn", "Worldwide"],
    address: {
      "@type": "PostalAddress",
      streetAddress: p.address?.street,
      postalCode: p.address?.postal_code,
      addressLocality: p.address?.locality,
      addressRegion: p.address?.region,
      addressCountry: p.address?.country,
    },
    geo: p.geo
      ? { "@type": "GeoCoordinates", latitude: p.geo.lat, longitude: p.geo.lng }
      : undefined,
    sameAs: p.same_as || [],
    knowsAbout: ["Search Engine Optimization", "Technical SEO", "Link building"],
  };
  if (p.aggregate_rating?.count) {
    s.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: p.aggregate_rating.value,
      reviewCount: p.aggregate_rating.count,
      bestRating: 5,
    };
  }
  if (Array.isArray(p.reviews) && p.reviews.length) {
    s.review = p.reviews.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.author },
      datePublished: r.date,
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
      reviewBody: lang === "da" ? r.text_da : r.text_en,
    }));
  }
  return s;
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: "Magnus Løv Schmidt",
    inLanguage: ["da-DK", "en"],
    publisher: { "@id": `${SITE_URL}/#person` },
  };
}

// Base schema graph injected on EVERY page (Website + Organization).
// Person is added only on Home + About (its main pages) to avoid redundancy.
export function baseGraph(p, lang = "da") {
  return {
    "@context": "https://schema.org",
    "@graph": [
      localBusinessSchema(p, lang),
      websiteSchema(),
    ],
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function serviceSchema(service, lang, path) {
  const c = service[lang] || service.da;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: c.title,
    serviceType: c.title,
    description: c.meta_description,
    url: `${SITE_URL}${path}`,
    provider: { "@id": `${SITE_URL}/#business` },
    areaServed: "Danmark",
  };
}

export function articleSchema(post, lang, path, authorName) {
  const c = post[lang] || post.da;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: c.title,
    description: c.meta_description || c.excerpt,
    image: post.cover,
    datePublished: post.date,
    dateModified: post.date,
    inLanguage: lang === "da" ? "da-DK" : "en",
    mainEntityOfPage: `${SITE_URL}${path}`,
    author: { "@id": `${SITE_URL}/#person`, name: authorName },
    publisher: { "@id": `${SITE_URL}/#person` },
  };
}

export function faqSchema(faq) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
